import { createReducer } from '@reduxjs/toolkit';

import { FriendsState, SortByOptions } from './types';
import { GlobalState } from '../../../types.global';
import { browserStorageUpdate } from '../../../store/actions';
import * as actions from './actions';
import { fetchLoggedUser } from '../../user/slice/actions';

// -------------

export const SORT_BY_OPTIONS: {value: SortByOptions, label: string}[] = [
  { value: 'shellsCounter', label: 'All time' },
];

const INIT_STATE = {
  isViewed: false,

  lastViewed: null,
  isOnboarded: false,
  openedHeaderPopover: null,
  friends: {
    showAll: false,
    isHidden: false,
    sortBy: SORT_BY_OPTIONS[0].value,
    newFriendsIds: [],
    list: [],
    sendTo: null,
    sendingState: null,
  },
  messages: {
    unreadIds: [],
    list: [],
  },
  $realtime: {
    isOnlineById: {},
    channelIdByFriendId: {},
  }
};

export const getInitialFriendsState = ({ friends }: GlobalState): FriendsState => friends ? ({
  ...INIT_STATE,

  isViewed: friends.isViewed || INIT_STATE.isViewed,
  lastViewed: friends.lastViewed || INIT_STATE.lastViewed,
  isOnboarded: friends.isOnboarded || INIT_STATE.isOnboarded,
  openedHeaderPopover: null,

  friends: friends.friends ? {
    ...INIT_STATE.friends,
    newFriendsIds: [],
    list: friends.friends?.list || [],
    sendTo: null,
    sendingState: null,
    showAll: friends.friends?.showAll || false,
    isHidden: false,
    sortBy: SORT_BY_OPTIONS[0].value,
  } : INIT_STATE.friends,
  messages: {
    unreadIds: friends.messages?.unreadIds || [],
    list: friends.messages?.list || [],
  },
}) : INIT_STATE;

const reducer = createReducer(null, (builder) => {
  builder
    .addCase(browserStorageUpdate, (state, { payload }) => {
      if (payload.isNewer) {
        return getInitialFriendsState(payload.state);
      }
    })
    .addCase(fetchLoggedUser.fulfilled, (state, { payload }) => {
      if (!payload) return;
      if (payload.data === false) {
        return INIT_STATE;
      }

      if (!payload.data) return;

      const newFriends = payload.data.friends || [];
      const oldFriendsIds = state.friends.list.map(({ id }) => id);

      state.friends.newFriendsIds = newFriends
        .map(({ id }) => id)
        .filter((id) => !oldFriendsIds.includes(id));

      state.friends.list = payload.data.friends || [];
      state.messages.list = payload.data?.messages || [];

      const newUnreadIds = state.messages.list
        .filter(({ friendFrom }) => friendFrom !== payload.data.user.id)
        .filter(({ received }) => !received)
        .map(({ id }) => id);

      state.messages.unreadIds = [
        ...state.messages.unreadIds,
        ...newUnreadIds,
      ].filter((id, i, array) => array.indexOf(id) === i);
    })
    .addCase(actions.sendMessage.pending, (state: FriendsState) => {
      state.friends.sendingState = { type: 'pending' };
    })
    .addCase(actions.sendMessage.fulfilled, (state, { payload }) => {
      state.friends.sendingState = { type: 'fulfilled' };
      state.messages.list.push(payload);
    })
    .addCase(actions.sendMessage.rejected, (state, { payload }) => {
      state.friends.sendingState = {
        type: 'rejected',
        error: payload,
      };
    })
    .addCase(actions.sawMessage.fulfilled, (state, { payload }) => {
      state.messages.list = state.messages.list.map((msg) =>
        msg.id === payload.id ? { ...msg, ...payload } : msg
      );
    })

    .addCase(actions.changeFriendsSort, (state, { payload }) => {
      state.friends.sortBy = payload.value;
    })
    .addCase(actions.toggleShowAll, (state: FriendsState) => {
      state.friends.showAll = !state.friends.showAll;
    })
    .addCase(actions.toggleHideFriends, (state: FriendsState) => {
      state.friends.isHidden = !state.friends.isHidden;
    })
    .addCase(actions.readMessage, (state: FriendsState, { payload }) => {
      state.messages.unreadIds = state.messages.unreadIds.filter(
        (id) => id !== payload.id
      );
    })
    .addCase(actions.toggleSendTo, (state: FriendsState, { payload }) => {
      state.friends.sendingState = null;
      if (payload) {
        state.friends.sendTo = payload;
        state.friends.isHidden = false;
      } else {
        state.friends.sendTo = null;
      }
    })
    .addCase(actions.deletedFriend, (state: FriendsState, { payload }) => {
      state.friends.list = state.friends.list.filter(
        ({ id }) => id !== payload.friendTo
      );
    })
    .addCase(actions.setHeaderPopover, (state: FriendsState, { payload }) => {
      state.openedHeaderPopover = payload;
      state.isViewed = true;
      state.lastViewed = Date.now();
      if (state.openedHeaderPopover === 'onboarding') {
        state.isOnboarded = true;
      }
    })
    .addCase(actions.clearNewFriends, (state: FriendsState) => {
      state.friends.newFriendsIds = [];
    })
    .addCase(actions.receivedMessage, (state: FriendsState, { payload }) => {
      state.messages.list.push(payload);
      state.messages.unreadIds.push(payload.id);
    })
    .addCase(actions.receivedSawMessage, (state: FriendsState, { payload }) => {
      state.messages.list = state.messages.list.map((msg) => msg.id === payload.id ? { ...msg, received: true } : msg);
    })
});

export default reducer;
